import { ContextMenu } from '@base-ui/react/context-menu';
import { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, KeyboardEvent, ReactElement } from 'react';
import MenuItemCore from './MenuItemCore';

export type TContextMenuItem = {
  label: string;
  onClick?: () => void;
  icon?: TIconName;
  disabled?: boolean;
  disabledReasons?: string[];
  /** Close the menu after selecting the item. Defaults to true; set to false to keep the menu open. */
  closeOnClick?: boolean;
};

export interface ContextMenuRootProps {
  menuItems: TContextMenuItem[];
  /**
   * Becomes the menu trigger via Base UI's `render` prop, so it must forward
   * its ref and spread the props it receives, or the menu won't open.
   */
  children: ReactElement;
  /** Render the child without attaching a menu. */
  disabled?: boolean;
}

// Base UI's trigger only opens on pointer/touch, so translate the keyboard
// context-menu keys (Shift+F10, ContextMenu) into the `contextmenu` event it
// understands, anchored to the trigger's box.
const openViaKeyboard = (event: KeyboardEvent<HTMLElement>) => {
  const isContextMenuKey = event.key === 'ContextMenu';
  const isShiftF10 = event.shiftKey && event.key === 'F10';

  if (!isContextMenuKey && !isShiftF10) {
    return;
  }

  event.preventDefault();

  const trigger = event.currentTarget;
  const rect = trigger.getBoundingClientRect();

  trigger.dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2,
    })
  );
};

/**
 * Attaches a context menu to an element, opening on right-click, touch long
 * press, or Shift+F10 / the Context Menu key. Pair it with a visible affordance
 * for users who cannot right-click (WCAG 2.1.1).
 */
const ContextMenuRoot: FC<ContextMenuRootProps> = (props) => {
  const { menuItems, children, disabled } = props;

  if (disabled || menuItems.length === 0) {
    return <>{children}</>;
  }

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger
        render={children as ReactElement<Record<string, unknown>>}
        tabIndex={0}
        aria-haspopup="menu"
        aria-keyshortcuts="Shift+F10 ContextMenu"
        onKeyDown={openViaKeyboard}
      />

      <ContextMenu.Portal>
        <ContextMenu.Positioner
          className="outline-none z-dropdown"
          side="bottom"
          align="start"
          sideOffset={2}
        >
          <ContextMenu.Popup
            className={clstx(
              'typography:prose min-w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-md outline-none',
              'transition-opacity duration-100 data-starting-style:opacity-0 data-ending-style:opacity-0'
            )}
          >
            {menuItems.map((item, index) => (
              <ContextMenu.Item
                key={index}
                label={item.label}
                disabled={item.disabled}
                closeOnClick={item.closeOnClick}
                onClick={item.onClick}
                render={
                  <MenuItemCore
                    as="div"
                    iconName={item.icon}
                    disabled={item.disabled}
                    disabledReasons={item.disabledReasons}
                    wrapperClassName="data-highlighted:bg-gray-100"
                  >
                    {item.label}
                  </MenuItemCore>
                }
              />
            ))}
          </ContextMenu.Popup>
        </ContextMenu.Positioner>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
};

export default ContextMenuRoot;
